/**
 * AdminFeedback — cross-org feedback intelligence for super admins.
 *
 * A high-density but calm dashboard. Fixed content-first layout:
 *   [KPI row]   Total volume · Helpful rate · High-band down-votes
 *   [Sparkline + Band donut side by side]
 *   [Per-org league table]
 *   [Recent down-votes triage list — the actionable heart of the page]
 *
 * Data source: /api/admin/feedback?days=N (super_admin only).
 * Deliberately no charts library — everything is CSS/SVG, so it stays fast
 * and matches the Crystallography Midnight aesthetic without importing 200KB
 * of recharts just for one sparkline.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ThumbsUp, ThumbsDown, TrendingUp, TrendingDown, ShieldCheck,
  ShieldAlert, ShieldQuestion, Shield, ArrowUpRight, Building2,
} from "lucide-react";

const BANDS = ["high", "moderate", "low", "uncertain"];
const BAND_META = {
  high:      { label: "GROUNDED",      color: "#10B981", Icon: ShieldCheck },
  moderate:  { label: "PARTIAL",       color: "#F59E0B", Icon: Shield },
  low:       { label: "WEAK",          color: "#2563FF", Icon: ShieldAlert },
  uncertain: { label: "UNCERTAIN",     color: "#6B7280", Icon: ShieldQuestion },
};

function pct(n) {
  if (n === null || n === undefined) return "—";
  return `${Math.round(n * 100)}%`;
}

/**
 * Sparkline — two-track SVG, up (accent) + down (red), 30 days.
 * No library. Just SVG polyline with math. Height fixed at 60px.
 */
function TimelineSpark({ timeline }) {
  const w = 640, h = 96, pad = 8;
  if (!timeline || timeline.length === 0) {
    return <div className="text-xs text-[var(--aria-text-muted)] italic">no activity yet</div>;
  }
  const maxV = Math.max(1, ...timeline.map(d => d.up + d.down));
  const stepX = (w - pad * 2) / Math.max(1, timeline.length - 1);
  const scaleY = (v) => h - pad - (v / maxV) * (h - pad * 2);
  const upPts = timeline.map((d, i) => `${pad + i * stepX},${scaleY(d.up)}`).join(" ");
  const downPts = timeline.map((d, i) => `${pad + i * stepX},${scaleY(d.down)}`).join(" ");
  const upArea = `M ${pad},${h - pad} L ${upPts.split(" ").join(" L ")} L ${w - pad},${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-24">
      <defs>
        <linearGradient id="feedback-up-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(16,185,129,0.35)" />
          <stop offset="100%" stopColor="rgba(16,185,129,0.02)" />
        </linearGradient>
      </defs>
      <path d={upArea} fill="url(#feedback-up-grad)" />
      <polyline points={upPts} fill="none" stroke="#10B981" strokeWidth="1.5" />
      <polyline points={downPts} fill="none" stroke="#2563FF" strokeWidth="1.5"
                strokeDasharray="3 2" opacity="0.9" />
    </svg>
  );
}

/**
 * BandDonut — pure SVG donut of confidence bands among down-votes. It answers
 * the question "which bands are we screwing up on?" — high-band downs are
 * the scariest because that's ARIA sounding confident and being wrong.
 */
function BandDonut({ distribution }) {
  const total = BANDS.reduce((s, b) => s + (distribution[b] || 0), 0);
  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-xs text-[var(--aria-text-muted)] italic">
        no down-votes in window
      </div>
    );
  }
  const size = 180, r = 70, cx = size / 2, cy = size / 2;
  const strokeW = 22;
  let acc = 0;
  const segs = BANDS.map((b) => {
    const v = distribution[b] || 0;
    if (v === 0) return null;
    const startFrac = acc / total;
    const frac = v / total;
    acc += v;
    const circumference = 2 * Math.PI * r;
    return {
      band: b,
      color: BAND_META[b].color,
      count: v,
      dash: `${frac * circumference} ${circumference}`,
      offset: -startFrac * circumference,
    };
  }).filter(Boolean);

  return (
    <div className="flex items-center gap-6">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-40 h-40 -rotate-90">
        {segs.map((s) => (
          <circle
            key={s.band}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={strokeW}
            strokeDasharray={s.dash}
            strokeDashoffset={s.offset}
          />
        ))}
      </svg>
      <div className="space-y-1.5 text-xs">
        {BANDS.map((b) => {
          const v = distribution[b] || 0;
          if (v === 0) return null;
          const M = BAND_META[b];
          return (
            <div key={b} className="flex items-center gap-2 font-mono tracking-[0.14em]" data-testid={`band-legend-${b}`}>
              <span className="inline-block w-2 h-2 rounded-sm" style={{ background: M.color }} />
              <M.Icon size={12} style={{ color: M.color }} />
              <span className="text-[var(--aria-text)]">{M.label}</span>
              <span className="text-[var(--aria-text-muted)]">· {v}</span>
              <span className="text-[var(--aria-text-faint)]">
                ({Math.round((v / total) * 100)}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** OrgLeagueTable — sorted, tabular per-org rollup with rate + volume bar. */
function OrgLeagueTable({ rows }) {
  const maxVol = Math.max(1, ...rows.map(r => r.total));
  return (
    <div className="aria-card overflow-hidden">
      <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center px-5 py-3 border-b border-[var(--aria-border)] text-[10px] font-mono tracking-[0.2em] text-[var(--aria-text-muted)] uppercase">
        <span>Organization</span>
        <span className="text-right">Volume</span>
        <span className="text-right">↑</span>
        <span className="text-right">↓</span>
        <span className="text-right">Rate</span>
      </div>
      <div className="divide-y divide-[var(--aria-border)]">
        {rows.length === 0 && (
          <div className="px-5 py-8 text-sm text-[var(--aria-text-muted)] italic">no org activity in window</div>
        )}
        {rows.map((o) => {
          const rate = o.helpful_rate;
          const rateTone =
            rate === null ? "text-[var(--aria-text-muted)]" :
            rate >= 0.8 ? "text-emerald-400" :
            rate >= 0.6 ? "text-amber-300" :
            "text-[var(--aria-primary)]";
          return (
            <div
              key={o.org_id}
              data-testid={`org-row-${o.org_id}`}
              className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center px-5 py-3 hover:bg-[var(--aria-surface-2)]/50 transition-colors"
            >
              <div className="min-w-0 flex items-center gap-2">
                <Building2 size={13} className="text-[var(--aria-text-muted)] shrink-0" />
                <span className="text-sm truncate font-medium">{o.org_name}</span>
                {o.plan && (
                  <span className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 border border-[var(--aria-border)] rounded-sm text-[var(--aria-text-muted)]">
                    {o.plan}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 justify-end w-40 shrink-0">
                <div className="h-[3px] w-24 bg-[var(--aria-surface-2)] overflow-hidden rounded-sm">
                  <div
                    className="h-full"
                    style={{
                      width: `${Math.round((o.total / maxVol) * 100)}%`,
                      background: "var(--aria-grad-brand)",
                    }}
                  />
                </div>
                <span className="text-xs font-mono tabular-nums text-[var(--aria-text-muted)] w-8 text-right">{o.total}</span>
              </div>
              <span className="text-xs text-emerald-400 font-mono tabular-nums w-10 text-right">{o.up}</span>
              <span className="text-xs text-red-400 font-mono tabular-nums w-10 text-right">{o.down}</span>
              <span className={`text-xs font-mono tabular-nums w-12 text-right ${rateTone}`}>{pct(rate)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function AdminFeedback() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [bandFilter, setBandFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/admin/feedback", { params: { days } });
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) toast.error("Couldn't load feedback intelligence");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const filteredDowns = useMemo(() => {
    if (!data) return [];
    if (bandFilter === "all") return data.recent_downs;
    return data.recent_downs.filter((r) => (r.confidence_band || "unknown") === bandFilter);
  }, [data, bandFilter]);

  const highBandDowns = data ? (data.band_distribution?.high || 0) : 0;
  const total = data ? data.totals.total : 0;
  const helpfulRate = data ? data.totals.helpful_rate : null;

  return (
    <div className="p-8 space-y-8 max-w-[1600px] mx-auto" data-testid="admin-feedback-page">
      {/* Page header */}
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] font-mono tracking-[0.28em] text-[var(--aria-primary)] uppercase mb-2">
            ◆ Super Admin · feedback intelligence
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
            Where ARIA gets it <span className="text-[var(--aria-primary)]">wrong</span>.
          </h1>
          <p className="text-sm text-[var(--aria-text-muted)] mt-2 max-w-xl">
            Cross-tenant feedback aggregated over the last {days} days. Start with the down-vote list — that's your weekly triage queue.
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="range-picker">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`range-${d}`}
              className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] border rounded-sm transition-colors ${
                d === days
                  ? "border-[var(--aria-primary)] text-[var(--aria-primary)] bg-[var(--aria-primary)]/6"
                  : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:border-[var(--aria-text)]"
              }`}
            >
              {d}d
            </button>
          ))}
          <Link
            to="/admin"
            className="ml-3 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] border border-[var(--aria-border)] hover:border-[var(--aria-text)] rounded-sm inline-flex items-center gap-1"
          >
            ← back to orgs
          </Link>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-row">
        <KpiCard
          testId="kpi-volume"
          label="Total feedback"
          value={total.toLocaleString()}
          sub={`${data?.per_org?.length || 0} orgs contributing`}
          Icon={TrendingUp}
        />
        <KpiCard
          testId="kpi-rate"
          label="Helpful rate"
          value={pct(helpfulRate)}
          sub={helpfulRate !== null && helpfulRate >= 0.7 ? "healthy" : helpfulRate !== null ? "watch this" : "no data"}
          tone={helpfulRate === null ? "muted" : helpfulRate >= 0.7 ? "good" : "warn"}
          Icon={ThumbsUp}
        />
        <KpiCard
          testId="kpi-highband-downs"
          label="Confident wrongs"
          value={highBandDowns.toString()}
          sub="down-votes on 'grounded' answers"
          tone={highBandDowns > 5 ? "bad" : highBandDowns > 0 ? "warn" : "good"}
          Icon={ShieldAlert}
        />
        <KpiCard
          testId="kpi-downs"
          label="Down-votes"
          value={data?.totals?.down?.toString() || "0"}
          sub={data?.totals?.up ? `vs ${data.totals.up} up-votes` : "—"}
          tone="warn"
          Icon={ThumbsDown}
        />
      </div>

      {/* Timeline + donut */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
        <div className="aria-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-mono tracking-[0.24em] text-[var(--aria-text-muted)] uppercase">Timeline</div>
              <h3 className="font-display text-lg tracking-tight">Volume by day</h3>
            </div>
            <div className="flex items-center gap-4 text-[10px] font-mono uppercase tracking-[0.18em]">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-[2px] bg-emerald-400" /> up
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-[2px] bg-[var(--aria-primary)]" style={{borderTop:"1.5px dashed"}} /> down
              </span>
            </div>
          </div>
          <TimelineSpark timeline={data?.timeline || []} />
        </div>
        <div className="aria-card p-5">
          <div className="text-[10px] font-mono tracking-[0.24em] text-[var(--aria-text-muted)] uppercase">Confidence band</div>
          <h3 className="font-display text-lg tracking-tight mb-4">Down-votes by band</h3>
          <BandDonut distribution={data?.band_distribution || {}} />
        </div>
      </div>

      {/* Org league */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono tracking-[0.24em] text-[var(--aria-text-muted)] uppercase">By tenant</div>
            <h3 className="font-display text-lg tracking-tight">Organizations · sorted by volume</h3>
          </div>
        </div>
        <OrgLeagueTable rows={data?.per_org || []} />
      </div>

      {/* Recent down-votes */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="text-[10px] font-mono tracking-[0.24em] text-[var(--aria-primary)] uppercase">Triage queue</div>
            <h3 className="font-display text-lg tracking-tight">Recent down-voted answers</h3>
          </div>
          <div className="flex items-center gap-1.5" data-testid="band-filter">
            {["all", ...BANDS].map((b) => (
              <button
                key={b}
                onClick={() => setBandFilter(b)}
                data-testid={`band-filter-${b}`}
                className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.16em] border rounded-sm transition-colors ${
                  b === bandFilter
                    ? "border-[var(--aria-primary)] text-[var(--aria-primary)] bg-[var(--aria-primary)]/6"
                    : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:border-[var(--aria-text)]"
                }`}
              >
                {b === "all" ? "all" : b}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {loading && (
            <div className="text-sm text-[var(--aria-text-muted)] italic">Loading…</div>
          )}
          {!loading && filteredDowns.length === 0 && (
            <div className="aria-card p-8 text-center text-sm text-[var(--aria-text-muted)] italic">
              Nothing to triage. ARIA is doing well {bandFilter !== "all" ? `for '${bandFilter}' band` : ""}.
            </div>
          )}
          {filteredDowns.map((d, i) => {
            const band = d.confidence_band || "unknown";
            const M = BAND_META[band] || BAND_META.uncertain;
            return (
              <div
                key={`${d.conversation_id}-${d.message_index}-${i}`}
                data-testid={`downvote-item-${i}`}
                className="aria-card p-4 flex gap-4 items-start"
              >
                <div className="shrink-0 flex flex-col items-center pt-1">
                  <M.Icon size={16} style={{ color: M.color }} />
                  <span className="text-[9px] font-mono uppercase tracking-[0.14em] mt-1"
                        style={{ color: M.color }}>
                    {M.label}
                  </span>
                  {typeof d.confidence === "number" && (
                    <span className="text-[9px] font-mono text-[var(--aria-text-muted)] tabular-nums mt-0.5">
                      {Math.round(d.confidence * 100)}%
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--aria-text-muted)] mb-1">
                    <span data-testid={`downvote-org-${i}`}>{d.org_name}</span>
                    <span>·</span>
                    <span>{relativeTime(d.updated_at)}</span>
                  </div>
                  <p className="text-sm text-[var(--aria-text)] line-clamp-3 leading-relaxed">
                    {d.answer_preview}
                    <span className="text-[var(--aria-text-faint)]">…</span>
                  </p>
                  {d.reason && (
                    <p className="mt-2 text-sm border-l-2 border-red-500/40 pl-3 py-1 bg-red-500/5 text-red-200/90 italic">
                      "{d.reason}"
                    </p>
                  )}
                  {d.sources?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {d.sources.slice(0, 4).map((s, j) => (
                        <span key={j} className="aria-pill text-[9px]">◆ {s}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="shrink-0">
                  <TrendingDown size={14} className="text-red-400" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, Icon, tone = "neutral", testId }) {
  const toneCls = {
    good:  "text-emerald-400",
    warn:  "text-amber-300",
    bad:   "text-[var(--aria-primary)]",
    muted: "text-[var(--aria-text-muted)]",
    neutral: "text-[var(--aria-text)]",
  }[tone];
  return (
    <div className="aria-card p-5 relative overflow-hidden group" data-testid={testId}>
      <div className="absolute -right-4 -top-4 opacity-[0.05] group-hover:opacity-[0.08] transition-opacity">
        <Icon size={100} />
      </div>
      <div className="text-[10px] font-mono tracking-[0.24em] text-[var(--aria-text-muted)] uppercase mb-2">
        {label}
      </div>
      <div className={`font-display text-4xl tracking-tight tabular-nums ${toneCls}`} data-testid={`${testId}-value`}>
        {value}
      </div>
      <div className="text-xs text-[var(--aria-text-muted)] mt-1 inline-flex items-center gap-1">
        {sub}
        {tone === "good" && <ArrowUpRight size={11} className="text-emerald-400" />}
      </div>
    </div>
  );
}
